import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IpcChannel, IpcV2Service } from 'src/app/services/ipc-v2.service';
import { LogService, LogEntry } from 'src/app/services/log.service';
import { Subscription } from 'rxjs';

interface LogEntryDisplay {
  id: number;
  level: string;
  message: string;
  source: string;
  timestamp: string;
  formattedTime: string;
  count?: number;
  isExpanded?: boolean;
  isLong?: boolean;
}

interface HistoryLogEntry extends LogEntryDisplay {
  originalLine: string;
}

@Component({
  selector: 'app-error',
  templateUrl: './error.component.html',
  styleUrls: ['./error.component.scss'],
})
export class ErrorComponent implements OnInit, OnDestroy {
  @ViewChild('logContainer') logContainer!: ElementRef<HTMLDivElement>;

  // History mode data
  historyLogs: HistoryLogEntry[] = [];
  availableDates: string[] = [];
  selectedDate: string = '';

  // Live mode data
  liveLogs: LogEntryDisplay[] = [];

  // UI state
  isLiveMode: boolean = true;
  isPaused: boolean = false;
  searchTerm: string = '';
  autoScroll: boolean = true;
  isLoading: boolean = false;

  // Cached filtered arrays
  cachedFilteredLiveLogs: LogEntryDisplay[] = [];
  cachedFilteredHistoryLogs: HistoryLogEntry[] = [];

  private liveSub?: Subscription;
  private nextLogId: number = 0;
  private readonly MAX_LIVE_LOGS = 500;

  // Batching for live logs (removed per user request)

  constructor(
    @Optional() public dialogRef: MatDialogRef<ErrorComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ipcV2: IpcV2Service,
    private readonly logService: LogService,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // Get available dates for the history tab
    this.ipcV2
      .sendAndReceive(IpcChannel.GET_AVAILABLE_LOG_DATES)
      .subscribe((res) => {
        if (res?.data) {
          this.availableDates = res.data;
          if (!this.selectedDate && this.availableDates.length > 0) {
            this.selectedDate = this.availableDates[0];
          }
          this.ref.detectChanges();
        }
      });

    // Start live mode by default
    this.subscribeLive();
  }

  // ── Live Mode ──

  private subscribeLive() {
    this.unsubscribeLive();
    this.liveSub = this.logService.log$.subscribe((entry) => {
      if (!this.isPaused) {
        this.processLiveLog(entry);
      }
    });
  }

  private unsubscribeLive() {
    this.liveSub?.unsubscribe();
    this.liveSub = undefined;
  }

  private processLiveLog(entry: LogEntry) {
    const last =
      this.liveLogs.length > 0 ? this.liveLogs[this.liveLogs.length - 1] : null;

    if (last && last.message === entry.message && last.level === entry.level) {
      last.count = (last.count || 1) + 1;
      last.timestamp = entry.timestamp;
      last.formattedTime = this.formatTimestamp(entry.timestamp);
    } else {
      const analysis = this.analyzeMessage(entry.message);
      this.liveLogs.push({
        id: this.nextLogId++,
        level: entry.level,
        message: analysis.truncatedMsg,
        source: entry.source,
        timestamp: entry.timestamp,
        formattedTime: this.formatTimestamp(entry.timestamp),
        count: 1,
        isLong: analysis.isLong,
        isExpanded: false,
      });
    }

    if (this.liveLogs.length > this.MAX_LIVE_LOGS) {
      this.liveLogs = this.liveLogs.slice(-this.MAX_LIVE_LOGS);
    }

    this.updateFilteredLive();
    if (this.autoScroll) {
      this.scrollToBottom();
    }
    this.ref.detectChanges();
  }

  // ── History Mode ──

  fetchLogs() {
    this.isLoading = true;
    this.ref.detectChanges();
    this.ipcV2
      .sendAndReceive(IpcChannel.GET_LOGS, this.selectedDate)
      .subscribe((res: any) => {
        if (res?.flatLogs) {
          this.historyLogs = this.parseHistoryLogs(res.flatLogs);
        } else if (res?.data && res.data[0]?.lines) {
          this.historyLogs = this.parseHistoryLogs(res.data[0].lines);
        }
        this.updateFilteredHistory();
        this.isLoading = false;
        this.ref.detectChanges();
      });
  }

  onDateChange(event: any) {
    this.selectedDate = event.target.value;
    this.fetchLogs();
  }

  // ── Tab switching ──

  toggleLiveMode(targetMode: 'live' | 'history') {
    if (
      (this.isLiveMode && targetMode === 'live') ||
      (!this.isLiveMode && targetMode === 'history')
    ) {
      return;
    }

    this.isLiveMode = targetMode === 'live';

    if (this.isLiveMode) {
      this.subscribeLive();
      this.scrollToBottom();
    } else {
      this.unsubscribeLive();
      this.fetchLogs();
    }
    this.ref.detectChanges();
  }

  // ── Filtering ──

  private updateFilteredLive() {
    if (!this.searchTerm) {
      this.cachedFilteredLiveLogs = this.liveLogs;
    } else {
      const term = this.searchTerm.toLowerCase();
      this.cachedFilteredLiveLogs = this.liveLogs.filter(
        (log) =>
          log.message.toLowerCase().includes(term) ||
          log.level.toLowerCase().includes(term),
      );
    }
  }

  private updateFilteredHistory() {
    if (!this.searchTerm) {
      this.cachedFilteredHistoryLogs = this.historyLogs;
    } else {
      const term = this.searchTerm.toLowerCase();
      this.cachedFilteredHistoryLogs = this.historyLogs.filter(
        (entry) =>
          entry.message.toLowerCase().includes(term) ||
          entry.level.toLowerCase().includes(term) ||
          entry.originalLine.toLowerCase().includes(term),
      );
    }
  }

  onSearchChange() {
    this.updateFilteredLive();
    this.updateFilteredHistory();
    this.ref.detectChanges();
  }

  clearSearch() {
    this.searchTerm = '';
    this.onSearchChange();
  }

  // ── Actions ──

  togglePause() {
    this.isPaused = !this.isPaused;
    this.ref.detectChanges();
  }

  toggleAutoScroll() {
    this.autoScroll = !this.autoScroll;
    if (this.autoScroll) {
      this.scrollToBottom();
    }
    this.ref.detectChanges();
  }

  clearLogs() {
    if (this.isLiveMode) {
      this.liveLogs = [];
      this.updateFilteredLive();
      this.ref.detectChanges();
    } else {
      this.ipcV2
        .sendAndReceive(IpcChannel.CLEAR_LOGS, this.selectedDate)
        .subscribe(() => this.fetchLogs());
    }
  }

  downloadLogs() {
    const list = this.isLiveMode
      ? this.cachedFilteredLiveLogs
      : this.cachedFilteredHistoryLogs;
    const lines = list.map((entry) => {
      const time = entry.formattedTime || this.formatTimestamp(entry.timestamp);
      const level = entry.level.toUpperCase().padEnd(7, ' ');
      const count = entry.count && entry.count > 1 ? `(${entry.count}x) ` : '';
      const source = entry.source ? `[${entry.source}] ` : '';
      return `[${time}] ${level} ${source}${count}- ${entry.message}`;
    });

    this.ipcV2
      .sendAndReceive(IpcChannel.DOWNLOAD_LOGS, {
        date: this.selectedDate || this.getTodayDate(),
        content: lines.join('\n'),
      })
      .subscribe();
  }

  // ── Helpers ──

  trackByLogId(_index: number, item: LogEntryDisplay): number {
    return item.id;
  }

  getLiveLogStyle(level: string): any {
    const styles: Record<string, any> = {
      error: { color: '#ff6b6b', bg: 'rgba(255, 107, 107, 0.1)' },
      warn: { color: '#ffd43b', bg: 'rgba(255, 212, 59, 0.1)' },
      info: { color: '#74c0fc', bg: 'rgba(116, 192, 252, 0.05)' },
      debug: { color: '#adb5bd', bg: 'rgba(173, 181, 189, 0.05)' },
      testing: { color: '#da77f2', bg: 'rgba(218, 119, 242, 0.1)' },
    };
    return styles[level] || { color: '#e9ecef', bg: 'transparent' };
  }

  getLogStyle(line: string): any {
    if (line.includes('error')) return { color: '#ff6b6b', fontWeight: '500' };
    if (line.includes('warn')) return { color: '#ffd43b', fontWeight: '500' };
    if (line.includes('[FRONTEND]')) return { color: '#74c0fc' };
    if (line.includes('[BACKEND]')) return { color: '#69db7c' };
    return { color: '#e9ecef' };
  }

  formatTimestamp(timestamp: string): string {
    try {
      const date = new Date(timestamp);
      const time = date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const ms = date.getMilliseconds().toString().padStart(3, '0');
      return `${time}.${ms}`;
    } catch {
      return timestamp;
    }
  }

  getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.logContainer?.nativeElement) {
        this.logContainer.nativeElement.scrollTop =
          this.logContainer.nativeElement.scrollHeight;
      }
    }, 10);
  }

  // ── Log file parsing (History mode only) ──

  private analyzeMessage(msg: string): {
    isLong: boolean;
    truncatedMsg: string;
  } {
    if (!msg) return { isLong: false, truncatedMsg: '' };

    let truncatedMsg = msg;
    const MAX_LEN = 100000;
    if (truncatedMsg.length > MAX_LEN) {
      truncatedMsg =
        truncatedMsg.substring(0, MAX_LEN) +
        '\n\n... [Truncated. Download logs for full content]';
    }

    let isLong = truncatedMsg.length > 500;
    if (!isLong) {
      let newlines = 0;
      for (let i = 0; i < truncatedMsg.length; i++) {
        if (truncatedMsg[i] === '\n') newlines++;
        if (newlines > 4) {
          isLong = true;
          break;
        }
      }
    }
    return { isLong, truncatedMsg };
  }

  private parseHistoryLogs(lines: string[]): HistoryLogEntry[] {
    if (!lines || lines.length === 0) return [];

    let idCounter = 0;
    let multilineBuffer: string[] = [];
    const parsed: HistoryLogEntry[] = [];

    // Re-assemble multiline log entries
    for (const line of lines) {
      const isHeader = /^\[(.*?)]\s+\[(.*?)]/.test(line);
      if (isHeader) {
        multilineBuffer.push(line);
        const fullMessage = multilineBuffer.reverse().join('\n');
        parsed.push(this.parseLogLine(fullMessage, idCounter++));
        multilineBuffer = [];
      } else {
        multilineBuffer.push(line);
      }
    }
    if (multilineBuffer.length > 0) {
      const fullMessage = multilineBuffer.reverse().join('\n');
      parsed.push(this.parseLogLine(fullMessage, idCounter++));
    }

    // Group consecutive identical messages
    if (parsed.length === 0) return [];
    const grouped: HistoryLogEntry[] = [];
    let current = parsed[0];
    for (let i = 1; i < parsed.length; i++) {
      const next = parsed[i];
      if (current.message === next.message && current.level === next.level) {
        current.count = (current.count || 1) + 1;
      } else {
        grouped.push(current);
        current = next;
      }
    }
    grouped.push(current);
    return grouped;
  }

  private parseLogLine(line: string, id: number): HistoryLogEntry {
    const analysis = this.analyzeMessage(line);
    const regex =
      /^\[(.*?)]\s+\[(.*?)]\s+(?:\[(FRONTEND|BACKEND)]\s+)?([\s\S]*)/;
    const match = line.match(regex);

    if (match) {
      const msg = match[4];
      const a = this.analyzeMessage(msg);
      return {
        id,
        timestamp: match[1],
        level: match[2].toLowerCase(),
        message: a.truncatedMsg,
        source: match[3] || 'BACKEND',
        originalLine: line,
        formattedTime: this.formatTimestamp(match[1]),
        count: 1,
        isLong: a.isLong,
        isExpanded: false,
      };
    }

    return {
      id,
      level: 'info',
      message: analysis.truncatedMsg,
      timestamp: this.getTodayDate(),
      source: 'BACKEND',
      originalLine: line,
      formattedTime: '',
      count: 1,
      isLong: analysis.isLong,
      isExpanded: false,
    };
  }

  ngOnDestroy(): void {
    this.unsubscribeLive();
    this.ipcV2.removeListener(IpcChannel.DOWNLOAD_LOGS);
    this.ipcV2.removeListener(IpcChannel.CLEAR_LOGS);
    this.ipcV2.removeListener(IpcChannel.GET_AVAILABLE_LOG_DATES);
    this.ipcV2.removeListener(IpcChannel.GET_LOGS);
  }
}
